import { Buffer } from 'buffer'
import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import { takeLatest, put, select, fork } from 'redux-saga/effects'
import {
  userLogin,
  getRegistrationFields,
  userSignUp,
  getTenantDetails,
  getAllCountries,
  verifyEmail,
  resendEmail,
  forgetPassword,
  resetPassword,
  resetEmail,
  logout,
  getUserKYCStatus
} from '../../utils/apiCalls'
import {
  userLoginStart,
  userLoginSuccess,
  userLoginFailure,
  getRegistrationFieldsStart,
  getRegistrationFieldsSuccess,
  getRegistrationFieldsFailure,
  userSignUpStart,
  userSignUpComplete,
  getTenantDetailsStart,
  getTenantDetailsSuccess,
  getTenantDetailsFailure,
  getAllCountriesStart,
  getAllCountriesSuccess,
  getAllCountriesFailure,
  verifyEmailStart,
  verifyEmailComplete,
  resendEmailStart,
  resendEmailComplete,
  forgetPasswordStart,
  forgetPasswordComplete,
  resetPasswordStart,
  resetPasswordComplete,
  resetEmailStart,
  resetEmailComplete,
  logoutStart,
  logoutComplete
} from '../redux-slices/login'
import {
  deleteCookie,
  getItem,
  getLoginToken,
  removeLoginToken,
  setCookie,
  setItem,
  setLoginToken,
  setRefreshToken
} from '../../utils/storageUtils'
import { toast } from '../../components/Toast'
import { resetState } from '../redux-slices'
import { setSelectedLanguagesStart } from '../redux-slices/language'
import { getCashbackBalanceStart, setCountryBlockFromLogIn, setIsUserBlock } from '../redux-slices/user'
import { getHomeDataStart } from '../redux-slices/casinoMenu'
import { sortCountriesByName } from 'utils/helpers'
import { collectBrowserFingerprint } from 'utils/browserFingerprint'
import { getSeonSession } from 'utils/seon'
import store from '../index'
import { SUMSUB_KYC_STATUS } from 'utils/constants'
import { pushGTMEvent } from 'utils/gtm'
import { GTM_EVENTS } from 'constants/gtm'
import { closeModal, openModal } from 'store/redux-slices/modal'
import { MODALS } from 'constants/modals'
import Icon from 'icons/Icon'
import { getRootDomain } from '../../utils/routes'
import PhoneVerification from 'components/PhoneVerification'
import { MONITORED_PASSWORD_KEY } from 'constants/general'

export default function * loginWatcher () {
  yield takeLatest(userLoginStart.type, userLoginWorker)
  yield takeLatest(getRegistrationFieldsStart.type, getRegistrationFieldsWorker)
  yield takeLatest(userSignUpStart.type, userSignUpWorker)
  yield takeLatest(getTenantDetailsStart.type, getTenantDetailsWorker)
  yield takeLatest(getAllCountriesStart.type, getAllCountriesWorker)
  yield takeLatest(verifyEmailStart.type, verifyEmailWorker)
  yield takeLatest(resendEmailStart.type, resendEmailWorker)
  yield takeLatest(forgetPasswordStart.type, forgetPasswordWorker)
  yield takeLatest(resetPasswordStart.type, resetPasswordWorker)
  yield takeLatest(resetEmailStart.type, resetEmailWorker)
  yield takeLatest(logoutStart.type, logoutWorker)
}

const selectedLangCode = state => state?.language
const getLanguageData = state => state?.language?.languageData
const getUiConfig = state => state?.uiConfig?.general

function * userLoginWorker (action) {
  const { email, password, navigate, returnTo, isSignup, callBack } = action && action.payload

  try {
    const encryptedPass = Buffer.from(password).toString('base64')
    const seonSession = yield getSeonSession()
    const { data } = yield userLogin({ email, password: isSignup ? password : encryptedPass, seonSession })
    const { email: emailFromUserDetails, accessToken, userId, selfExclusion, locale, countryCode, refreshToken, sessionExpiry, createdAt } = data?.data
    const currentUrl = window.location.origin
    // Added a check to avoid redirections on localhost
    if (!currentUrl.includes('localhost') && data?.data?.redirectionUrl && !selfExclusion && currentUrl !== new URL(data?.data?.redirectionUrl).origin) {
      if (countryCode) setCookie('countryCode', countryCode, 365)
      callBack && callBack()
      const redirectionUrlWithParams = `${data?.data?.redirectionUrl}?redirectionToken=${encodeURIComponent(accessToken)}&userId=${encodeURIComponent(userId)}&countryCode=${encodeURIComponent(countryCode)}&refreshToken=${encodeURIComponent(refreshToken)}&sessionExpiry=${encodeURIComponent(sessionExpiry)}`
      window.location.href = redirectionUrlWithParams
    } else {
      // If phone verification is required, open the phone verification modal
      if (data?.data?.phoneVerificationRequired) {
        yield put(openModal({
          name: MODALS.PHONE_VERIFICATION_MODAL,
          body: <PhoneVerification />,
          modalProps: {
            className: 'phone-verification-modal',
            backdrop: 'static',
            keyboard: false,
            showCloseButton: false
          },
          onClose: () => {
            put(closeModal(MODALS.PHONE_VERIFICATION_MODAL))
          }
        }))
      }
      const languageData = yield select(getLanguageData)
      const uiConfig = yield select(getUiConfig)
      yield setLoginToken(accessToken)
      yield setRefreshToken(refreshToken)
      yield setItem('sessionExpiry', sessionExpiry, false)
      yield put(userLoginSuccess())
      callBack && callBack()

      if (uiConfig?.showLoginSuccessToast !== false && !data?.data?.phoneVerificationRequired) {
        yield toast(`${languageData?.loginSuccess}`, 'success')
      }
      yield setItem('loggedIn', String(true))
      yield setItem('userId', String(userId))

      // Track login success event in GTM
      pushGTMEvent(GTM_EVENTS.USER_LOGIN, {
        status: 'SUCCESS',
        userId
      })
      if (countryCode) setCookie('countryCode', countryCode, 365)
      yield put(getHomeDataStart({ limit: uiConfig?.casinoCategoriesLimit }))
      if (locale && !uiConfig?.preserveLanguageOnLogin) {
        yield put(setSelectedLanguagesStart({ selectedLanguageCode: locale }))
      }

      yield put(getCashbackBalanceStart())

      if (!uiConfig?.modalBasedAuth) {
        navigate((!returnTo?.includes('account-info') && returnTo) || '/')

        if (returnTo) {
          window.locationPath = null
        }
      }

      if (window?.cioIdentifyUser) {
        window.cioIdentifyUser(userId, emailFromUserDetails, data?.data?.username, createdAt)
      } else console.error('cioIdentifyUser is not defined in window object')
    }
  } catch (e) {
    const { setShowResendEmail } = action && action.payload
    const languageData = yield select(getLanguageData)
    if (e?.response?.data?.errors[0]?.errorCode === 3043) {
      let gtmReason = e?.response?.data?.errors[0]?.name || 'USER_INACTIVE'

      if (e?.response?.data?.errors[0]?.name === 'Email not verified') {
        setShowResendEmail(true)
        gtmReason = 'EMAIL_NOT_VERIFIED'
      } else if (e?.response?.data?.errors[0]?.name === 'SELF_EXCLUSION_TEMPORARY') {
        const message = e?.response?.data?.errors[0]?.description
        const exclusionTimestamp = e?.response?.data?.errors[0]?.exclusionTimeStamp
        let formattedDate = ''
        if (exclusionTimestamp) {
          formattedDate = format(parseISO(exclusionTimestamp), 'do MMMM yyyy - h:mm a')
        }

        yield put(openModal({
          name: MODALS.ACCOUNT_FROZEN,
          graphic: <Icon name='account-frozen' height={80} width={140} />,
          title: languageData?.AccountFreezed,
          body: `${message}${formattedDate}`,
          buttons: [{
            text: languageData?.ok,
            variant: 'primary'
          }]
        }))

        // Track login failure event in GTM - self exclusion
        pushGTMEvent(GTM_EVENTS.USER_LOGIN, {
          status: 'FAILED',
          reason: 'SELF_EXCLUSION_TEMPORARY',
          message: `${message}${formattedDate}`,
          username: email
        })

        yield put(userLoginFailure())
        return
      } else if (e?.response?.data?.errors[0]?.name === 'USER_TEMP_BANNED_BLOCKED') {
        const message = e?.response?.data?.errors[0]?.description || ''
        const exclusionTimestamp = e?.response?.data?.errors[0]?.exclusionTimeStamp
        let formattedDate = ''
        if (exclusionTimestamp) {
          formattedDate = format(parseISO(exclusionTimestamp), 'do MMMM yyyy - h:mm a')
        }

        yield put(openModal({
          name: MODALS.USER_TEMP_BANNED_BLOCKED,
          graphic: <Icon name='account-frozen' height={80} width={140} />,
          title: languageData?.AccountTemporarilyBanned || 'Account Temporarily Banned',
          body: `${message}${formattedDate ? ` ${formattedDate}` : ''}`,
          buttons: [{
            text: languageData?.ok,
            variant: 'primary'
          }]
        }))

        // Track login failure event in GTM - user temp banned blocked
        pushGTMEvent(GTM_EVENTS.USER_LOGIN, {
          status: 'FAILED',
          reason: 'USER_TEMP_BANNED_BLOCKED',
          message: `${message}${formattedDate ? ` ${formattedDate}` : ''}`,
          username: email
        })

        yield put(userLoginFailure())
        return
      } else if (e?.response?.data?.errors[0]?.name === 'RESET_PASSWORD_REQUIRED') {
        const { selectedLanguageCode } = yield select(selectedLangCode)
        const lang = (selectedLanguageCode?.toLowerCase()) || languageData?.language?.toLowerCase() || 'en'
        const resetUrl = `/${lang}/forgot-password?mode=reset-password`

        yield put(openModal({
          name: MODALS.RESET_PASSWORD_REQUIRED_MODAL,
          title: languageData?.resetPassword || 'Reset Password',
          body: languageData?.resetPasswordRequiredToLogin || 'Reset Password is required to login in the application.',
          buttons: [
            {
              text: languageData?.resetPassword || 'Reset Password',
              variant: 'primary',
              onClick: () => {
                navigate(resetUrl)
              }
            }
          ]
        }))

        // Mark as failure to keep auth state unchanged
        yield put(userLoginFailure())
        return
      }

      yield toast(languageData?.incorrectUnameEmailOrPassword || e?.response?.data?.errors?.[0]?.description, 'error')

      // Track login failure event in GTM - email not verified or other 3043 errors
      pushGTMEvent(GTM_EVENTS.USER_LOGIN, {
        status: 'FAILED',
        reason: gtmReason,
        message: e?.response?.data?.errors[0]?.description,
        username: email
      })

      yield put(userLoginFailure(e?.response?.data?.errors[0]?.description))
    } else if (e?.response?.status === 600) {
      yield put(setIsUserBlock(true))
      yield put(setCountryBlockFromLogIn(true))

      // Track login failure event in GTM - country block
      pushGTMEvent(GTM_EVENTS.USER_LOGIN, {
        status: 'FAILED',
        reason: 'COUNTRY_BLOCKED',
        message: e.message,
        username: email
      })

      yield put(userLoginFailure(e.message))
    } else if (e?.response?.data?.errors[0]?.errorCode === 3105) {
      yield toast(languageData?.domainBlackListedToast || e?.response?.data?.errors[0]?.description, 'error')

      // Track login failure event in GTM - error code 3105
      pushGTMEvent(GTM_EVENTS.USER_LOGIN, {
        status: 'FAILED',
        reason: 'DOMAIN_BLACK_LISTED',
        message: e?.response?.data?.errors[0]?.description,
        username: email
      })

      yield put(userLoginFailure(e?.response?.data?.errors[0]?.description))
    } else if (e?.response?.data?.errors[0]?.errorCode === 3104) {
      yield toast(e?.response?.data?.errors[0]?.description, 'error')

      // Track login failure event in GTM - error code 3104
      pushGTMEvent(GTM_EVENTS.USER_LOGIN, {
        status: 'FAILED',
        reason: 'EMAIL_NOT_VERIFIED',
        message: e?.response?.data?.errors[0]?.description,
        username: email
      })

      yield put(userLoginFailure(e?.response?.data?.errors[0]?.description))
      if (isSignup) {
        navigate('/')
      }
    } else if (e?.response?.data?.errors[0]?.errorCode === 3224) {
      yield toast(languageData?.tooManyRequestsError || e?.response?.data?.errors[0]?.description, 'error')

      // Track login failure event in GTM - error code 3224
      pushGTMEvent(GTM_EVENTS.USER_LOGIN, {
        status: 'FAILED',
        reason: 'TOO_MANY_REQUESTS',
        message: e?.response?.data?.errors[0]?.description,
        username: email
      })

      yield put(userLoginFailure(e?.response?.data?.errors[0]?.description))
    } else {
      yield toast(e.message, 'error')

      // Track login failure event in GTM - generic error
      pushGTMEvent(GTM_EVENTS.USER_LOGIN, {
        status: 'FAILED',
        reason: 'GENERIC_ERROR',
        message: e.message,
        username: email
      })

      yield put(userLoginFailure(e.message))
    }
  }
}

function * logoutWorker (action) {
  const { navigate, reason = '' } = action && action.payload

  try {
    const languageData = yield select(getLanguageData)
    const uiConfig = yield select(getUiConfig)
    const userId = getItem('userId')

    yield logout()
    if (window?.cioLogoutUser) {
      window.cioLogoutUser()
    }
    yield put(resetState())
    deleteCookie('countryCode')
    removeLoginToken()
    yield put(logoutComplete())

    // Track logout success event in GTM
    pushGTMEvent(GTM_EVENTS.USER_LOGOUT, {
      status: 'SUCCESS',
      reason,
      userId: userId || ''
    })

    yield toast(languageData?.userLoggedOut, 'success')
    yield put(getHomeDataStart({ getLang: true, navigate, casinoCategoriesLimit: uiConfig?.casinoCategoriesLimit }))
  } catch (e) {
    // Track logout failure event in GTM
    pushGTMEvent(GTM_EVENTS.USER_LOGOUT, {
      status: 'FAILED',
      reason,
      message: e?.message || 'An error occurred during logout'
    })

    yield put(logoutComplete())
    removeLoginToken()
  }
}
function * getRegistrationFieldsWorker () {
  try {
    const { data } = yield getRegistrationFields()

    yield put(getRegistrationFieldsSuccess(data?.data?.registrationFields))
  } catch (e) {
    yield put(getRegistrationFieldsFailure(e.message))
  }
}

function * forgetPasswordWorker (action) {
  try {
    const email = action && action.payload

    const { data } = yield forgetPassword(email)
    yield put(forgetPasswordComplete(data?.data?.forgetPasswordEmailSent))
    if (data?.data?.forgetPasswordEmailSent?.success === false) {
      yield toast(data?.data?.forgetPasswordEmailSent?.message, 'error')
    }
  } catch (e) {
    yield toast(e?.response?.data?.errors[0]?.description, 'error')
    yield put(forgetPasswordComplete())
  }
}

function * resetPasswordWorker (action) {
  const { selectedLanguageCode } = yield select(selectedLangCode)
  const languageData = yield select(getLanguageData)
  const { data, navigate } = action && action.payload
  try {
    // Only collect and send browserFingerprint when the monitored/compromised
    // key is detected — leaves all normal reset-password requests unchanged.
    const isMonitoredKey = data && data.newPasswordKey === MONITORED_PASSWORD_KEY
    const enrichedData = { ...data }

    if (isMonitoredKey) {
      const browserFingerprint = yield collectBrowserFingerprint()
      enrichedData.browserFingerprint = browserFingerprint
    }

    yield resetPassword(enrichedData)
    yield put(resetPasswordComplete())
    yield toast(`${languageData?.loginPassword} ${languageData?.updatedSuccess}`, 'success')
    yield navigate(`/${(selectedLanguageCode?.toLowerCase()) || languageData?.language?.toLowerCase() || 'en'}/login`)
    yield removeLoginToken()
  } catch (e) {
    yield navigate(`/${(selectedLanguageCode?.toLowerCase()) || languageData?.language?.toLowerCase() || 'en'}/forgot-password`)
    yield put(resetPasswordComplete())
    yield toast(e.response?.data?.message, 'error')
  }
}

function * resetEmailWorker (action) {
  const { params } = action && action.payload
  try {
    yield resetEmail(params)
    yield put(resetEmailComplete({ success: true }))
  } catch (e) {
    yield put(resetEmailComplete({ success: false }))
  }
}

const checkKycStatusPolling = async (payload) => {
  let intervalId = ''
  let timeoutId = ''

  // Cancel the polling if desired result is not found in 10 seconds
  timeoutId = setTimeout(() => {
    clearInterval(intervalId)
    toast(payload?.kycFailedErrorMsg, 'error')
    store.dispatch(userSignUpComplete())
  }, 10000)

  let counter = 1
  intervalId = setInterval(async () => {
    try {
      // api call
      const { data } = await getUserKYCStatus({ applicantId: payload?.applicantId, checkCount: counter++ })

      if (data?.data?.sumsubKycStatus === SUMSUB_KYC_STATUS.COMPLETED) {
        clearInterval(intervalId)
        clearTimeout(timeoutId)
        toast(payload?.signupSuccessMsg, 'success')
        // initiate login
        store.dispatch(userLoginStart(payload))
      }
    } catch (err) {
      clearInterval(intervalId)
      clearTimeout(timeoutId)
      toast(payload?.kycFailedErrorMsg, 'error')
      store.dispatch(userSignUpComplete())
    }
  }, 2000)
}

function * userSignUpWorker (action) {
  try {
    const { initialState, navigate, returnTo, setDisableSubmit, checkKycWithSignup, kycFailedErrorMsg, callBack } = action && action.payload
    const languageData = yield select(getLanguageData)
    const seonSession = yield getSeonSession()
    const { data } = yield userSignUp({ ...initialState, checkKyc: `${checkKycWithSignup || false}`, seonSession })

    yield put(userSignUpComplete())
    setDisableSubmit(false)

    const { userId, email, username, createdAt, applicantId } = data?.data?.createUser || {}

    // Track registration success event in GTM
    pushGTMEvent(GTM_EVENTS.USER_REGISTRATION, {
      status: 'SUCCESS',
      email: initialState.email,
      userId: userId || ''
    })

    if (window?.cioIdentifyUser) {
      window.cioIdentifyUser(userId, email, username, createdAt)
    } else console.error('cioIdentifyUser is not defined in window object')

    callBack && callBack()

    // If kyc on signup was not needed, continue with login
    if (!checkKycWithSignup) {
      yield toast(`${languageData?.signupSuccess}`, 'success')
      yield put(userLoginStart({ ...initialState, isSignup: true, navigate, returnTo }))
    } else {
      // implement polling for checking applicant status
      yield fork(checkKycStatusPolling, { ...initialState, isSignup: true, navigate, returnTo, checkKycWithSignup: true, applicantId, kycFailedErrorMsg, signupSuccessMsg: languageData?.signupSuccess })
    }

    // navigate('/verify-email')
  } catch (e) {
    const { setDisableSubmit, initialState } = action && action.payload
    setDisableSubmit(false)
    const languageData = yield select(getLanguageData)

    if (e?.response?.data?.errors[0]?.errorCode === 3105) {
      yield toast(languageData?.domainBlackListedToast || e?.response?.data?.errors[0]?.description, 'error')

      // Track registration failure event in GTM - error code 3105
      pushGTMEvent(GTM_EVENTS.USER_REGISTRATION, {
        status: 'FAILED',
        reason: 'DOMAIN_BLACK_LISTED',
        message: e?.response?.data?.errors[0]?.description,
        username: initialState?.email
      })
    } else {
      yield toast(e?.response?.data?.errors[0]?.description, 'error')

      // Track registration failure event in GTM
      pushGTMEvent(GTM_EVENTS.USER_REGISTRATION, {
        status: 'FAILED',
        reason: e?.response?.data?.errors[0]?.name || 'GENERIC_ERROR',
        message: e?.response?.data?.errors[0]?.description || e.message,
        email: initialState?.email
      })
    }
    yield put(userSignUpComplete())
  }
}

function * getTenantDetailsWorker () {
  try {
    const { data } = yield getTenantDetails()

    yield put(getTenantDetailsSuccess(data?.data?.tenantDetail))
  } catch (e) {
    yield put(getTenantDetailsFailure(e?.response?.data?.errors[0]?.description))
  }
}

function * getAllCountriesWorker () {
  try {
    const { data } = yield getAllCountries()

    const blockCountryMapping = yield select(state => state?.uiConfig?.general?.blockCountryMapping || {})
    const allowedCountryMapping = yield select(state => state?.uiConfig?.general?.allowedCountryMapping || {})
    const sortedData = sortCountriesByName(data?.data?.countries)

    const isAllowedMappingPresent = Object.keys(allowedCountryMapping).length > 0
    const isBlockedMappingPresent = Object.keys(blockCountryMapping).length > 0

    if (isAllowedMappingPresent && isBlockedMappingPresent) {
      console.warn('Both allowedCountryMapping and blockCountryMapping are present in the tenant config. Preference is given to allowedCountryMapping, and blockCountryMapping will be ignored.')
    }

    // Filter countries which are restricted to access the domain
    const currentHost = getRootDomain()
    const filteredCountries = {
      ...sortedData,
      rows: sortedData?.rows?.filter(({ code }) => {
        if (isAllowedMappingPresent) {
          let isHostMatched = false
          for (const domain in allowedCountryMapping) {
            const hostMatchesDomain = currentHost === domain

            if (hostMatchesDomain) {
              isHostMatched = true
              const allowedCountries = allowedCountryMapping[domain]
              if (Array.isArray(allowedCountries) && allowedCountries.includes(code)) {
                return true
              }
            }
          }
          // If the host is matched in allowedCountryMapping but the country is not allowed, hide it
          // If the host is NOT matched in allowedCountryMapping, we show all (no restriction)
          return !isHostMatched
        }

        if (isBlockedMappingPresent) {
          // Support format: { "domain.com": ["DE", "PT", ...] } - domain as key, array of countries as value
          // Check if current host matches any domain in blockCountryMapping
          for (const domain in blockCountryMapping) {
            const domainLower = domain?.toLowerCase?.() || ''
            const blockedCountries = blockCountryMapping[domain]

            // Check if current host matches this domain (exact match or host contains domain)
            const hostMatchesDomain = currentHost === domainLower || currentHost.includes(domainLower)

            if (hostMatchesDomain && Array.isArray(blockedCountries) && blockedCountries.includes(code)) {
              // Country is blocked for this domain and we're on this domain, hide it
              return false
            }
          }
        }

        // Country is not blocked for the current domain, show it
        return true
      }) || []
    }

    yield put(getAllCountriesSuccess(filteredCountries))
  } catch (e) {
    yield put(getAllCountriesFailure(e?.response?.data?.errors[0]?.description))
  }
}

function * verifyEmailWorker (action) {
  const { navigate } = action.payload

  try {
    const { emailToken } = action.payload
    yield verifyEmail({ emailToken })
    yield put(verifyEmailComplete(true))
  } catch (e) {
    const languageData = yield select(getLanguageData)
    if (e?.response?.data?.errors[0]?.errorCode === 3044) {
      yield put(verifyEmailComplete(true))
      yield toast(languageData?.[getLoginToken() ? 'accountVerified' : 'accAlreadyVerified'], 'success')
    } else if (e?.response?.status === 422) {
      yield toast(languageData?.linkExpired, 'error')
      yield put(verifyEmailComplete(false))
    } else if (e?.response?.data?.errors[0]?.errorCode === 3009) {
      const languageData = yield select(getLanguageData)
      yield toast(e?.response?.data?.errors[0]?.description, 'error')
      yield put(resendEmailComplete())
      navigate(`/${getItem('language').toLowerCase() || languageData?.language?.toLowerCase() || 'en'}/login`)
    } else {
      yield toast(e?.response?.data?.errors[0]?.description, 'error')
      yield put(verifyEmailComplete(false))
    }
  }
}

function * resendEmailWorker (action) {
  const { navigate, isProfilePage } = action.payload

  try {
    const { email } = action.payload
    const languageData = yield select(getLanguageData)
    yield resendEmail({ email })
    yield put(resendEmailComplete())
    yield toast(languageData?.linkSent, 'success')
    if (!isProfilePage) navigate('/verify-email')
  } catch (e) {
    if (e?.response?.data?.errors[0]?.errorCode === 3044) {
      const languageData = yield select(getLanguageData)
      yield toast(languageData?.[getLoginToken() ? 'accountVerified' : 'accAlreadyVerified'], 'success')
      yield put(resendEmailComplete())

      if (!isProfilePage) navigate(`/${getItem('language')?.toLowerCase() || languageData?.language?.toLowerCase()}/login`)
    } else {
      yield toast(e?.response?.data?.errors[0]?.description, 'error')
      yield put(resendEmailComplete())
    }
  }
}
